import { memo, useCallback, useState, type MouseEvent } from 'react'
import { Handle, Position, type NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import type { DestinationNode as DestinationNodeType, PipelineNode } from '../../types/canvas.types'
import { areNodePropsEqual } from '../nodeMemo.util'
import styles from './DestinationNode.module.scss'
import { cn } from '@/shared/lib/utils'
import { useAppDispatch } from '@/app/reduxHooks'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { DataSourceType } from '@/features/adp/types/data-source.types'
import { useAdpSessionStore } from '@/features/adp/hooks/useAdpSessionStore'
import { store } from '@/app/store'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import { requiresUpstreamSource } from '../../canvas.utils'
import { TruncatedSecondaryTitle } from '../TruncatedSecondaryTitle'
import NodeValidationBadge from '../../validation/NodeValidationBadge'

const DestinationNode = memo(({ id, data }: NodeProps<DestinationNodeType>) => {
  const [expanded, setExpanded] = useState(true)
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow()
  const dispatch = useAppDispatch()
  const { removeNode } = useAdpSessionStore()
  const locked = Boolean(data.locked)

  const stop = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
  }, [])

  const handleSettings = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      stop(e)

      if (!requiresUpstreamSource(id, getNodes() as PipelineNode[], getEdges(), 'Destination')) {
        return
      }

      if (data.mode == null) {
        const isPicker = [DataSourceType.RDBMS, DataSourceType.NO_SQL].includes(
          data.destinationType as DataSourceType
        )

        const type = isPicker ? 'destination-picker' : 'destination-configuration'

        const overlayData = {
          nodeId: id,
          sourceType: data.destinationType,
          sourceId: data.originalId,
          isOpenedDirectly: !isPicker,
        }

        dispatch(openOverlay({ type, data: overlayData }))
        return
      }

      dispatch(
        openOverlay({
          type: 'destination-configuration',
          data: {
            nodeId: id,
            sourceType: data.destinationType,
            sourceId: data.originalId,
          },
        })
      )
    },
    [data.destinationType, data.mode, dispatch, getEdges, getNodes, id]
  )

  const handleToggleExpand = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    stop(e)
    setExpanded(v => !v)
  }, [])

  const handleLock = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      stop(e)
      setNodes(nodes =>
        nodes.map(node => {
          if (node.id !== id) return node

          const nextLocked = !(node.data as DestinationNodeType['data']).locked

          return {
            ...node,
            draggable: !nextLocked,
            data: {
              ...node.data,
              locked: nextLocked,
            },
          }
        })
      )
    },
    [id, setNodes]
  )

  const handleDelete = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      stop(e)

      const overlay = store.getState().overlay

      const overlayNodeId =
        overlay.data && typeof overlay.data === 'object' && 'nodeId' in overlay.data
          ? overlay.data.nodeId
          : null

      if (overlay.isOpen && overlayNodeId != null && String(overlayNodeId) === String(id)) {
        dispatch(closeOverlayAsync())
      }
      setNodes(nodes => nodes.filter(n => n.id !== id))
      setEdges(edges => edges.filter(e => e.source !== id && e.target !== id))
      removeNode(id)
    },
    [id, setEdges, setNodes]
  )

  const renderExpandButton = () => (
    <Button
      variant="ghost"
      size="icon"
      className={`${styles.headerIconBtn} nodrag`}
      aria-expanded={expanded}
      aria-label={expanded ? 'Collapse' : 'Expand'}
      onPointerDown={stop}
      onClick={handleToggleExpand}
    >
      <MaterialIcon name="unfold_more" size={18} />
    </Button>
  )

  const renderContent = (small = false) => (
    <>
      {data.image && (
        <div className={small ? styles.logoWellSm : styles.imageWrapper}>
          <img
            src={data.image}
            alt={data.nodeTitle}
            className={small ? styles.logoImgSm : styles.image}
          />
        </div>
      )}
      <div className={styles.titles}>
        <div className={styles.title}>{data.nodeTitle}</div>
        {data.nodeSecondaryTitle && (
          <TruncatedSecondaryTitle
            text={data.nodeSecondaryTitle}
            className={styles.secondaryTitle}
          />
        )}
      </div>
    </>
  )

  return (
    <div className={styles.node} data-validation={data.validation?.severity}>
      <NodeValidationBadge nodeId={id} validation={data.validation} />
      <Handle
        type="target"
        position={Position.Left}
        className={styles.handle}
        isConnectable={!locked}
      />

      <div className={styles.cardInner}>
        {/* HEADER */}
        <div className={cn(styles.header, expanded ? undefined : styles.headerCollapsed)}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Destination</span>

            {expanded && (
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.headerIconBtn} nodrag`}
                aria-label="Settings"
                onPointerDown={stop}
                onClick={handleSettings}
              >
                <MaterialIcon name="settings" size={16} />
              </Button>
            )}
          </div>

          {renderExpandButton()}
        </div>

        {/* BODY */}
        {expanded ? (
          <>
            <div className={styles.body}>{renderContent(false)}</div>

            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.footerBtn} nodrag`}
                aria-label={locked ? 'Unlock node' : 'Lock node'}
                aria-pressed={locked}
                onPointerDown={stop}
                onClick={handleLock}
              >
                <MaterialIcon name={locked ? 'lock' : 'lock_open'} size={18} />
              </Button>
              <div className={styles.footerRight}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} ${styles.footerDelete} nodrag`}
                  aria-label="Delete"
                  onPointerDown={stop}
                  onClick={handleDelete}
                >
                  <MaterialIcon name="delete" size={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.collapsedRow}>{renderContent(true)}</div>
        )}
      </div>
    </div>
  )
}, areNodePropsEqual)

DestinationNode.displayName = 'DestinationNode'

export { DestinationNode }
